(() => {
  const ERROR_DATASET_KEYS = {
    already_used: 'otpUsedError',
    email_required: 'otpEmailRequiredError',
    expired: 'otpExpiredError',
    invalid_code: 'otpInvalidCodeError',
    invalid_email: 'otpEmailInvalidError',
    not_found: 'otpNotFoundError',
    oidc_email_missing: 'otpEmailRequiredError',
    phone_mismatch: 'otpPhoneMismatchError',
    rate_limited: 'otpRateLimitError',
    registration_required: 'otpRegistrationError',
    shopify_customer_create_failed: 'otpEmailUnavailableError',
    shopify_customer_update_failed: 'otpEmailUnavailableError',
    shopify_phone_ambiguous: 'otpAccountUnavailableError',
    terms_required: 'otpTermsError',
    too_many_attempts: 'otpAttemptsError',
  };

  class OtpServiceError extends Error {
    constructor(message, status, code) {
      super(message);
      this.name = 'OtpServiceError';
      this.status = status;
      this.code = code;
    }
  }

  class LetoileOtpAuth {
    constructor(root = document) {
      this.activeForm = null;
      this.formStates = new WeakMap();
      this.lastTrigger = null;
      this.bind(root);
      this.bindGlobalEvents();
    }

    bind(root = document) {
      this.elements(root, '[data-otp-auth]')
        .filter((form) => !form.dataset.otpAuthBound)
        .forEach((form) => this.bindForm(form));
    }

    bindGlobalEvents() {
      document.addEventListener(
        'click',
        (event) => {
          const trigger = event
            .composedPath()
            .find((element) => element instanceof Element && element.matches('[data-otp-auth-trigger]'));
          if (!trigger) return;

          const form = document.querySelector('[data-otp-auth]');
          if (!form) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          trigger.closest('details')?.removeAttribute('open');
          this.open(form, trigger);
        },
        true,
      );

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.activeForm) return;
        const state = this.formStates.get(this.activeForm);
        if (!state?.redirecting) this.close(this.activeForm, state);
      });
    }

    bindForm(form) {
      form.dataset.otpAuthBound = 'true';

      const state = {
        abortController: null,
        flow: '',
        oidcLoginHint: '',
        phone: '',
        redirecting: false,
        requestId: '',
        requestSequence: 0,
        resendAvailableAt: 0,
        timerId: 0,
      };
      this.formStates.set(form, state);
      const phoneInput = form.querySelector('[data-otp-phone]');
      const emailInput = form.querySelector('[data-otp-email]');
      const codeInput = form.querySelector('[data-otp-code]');
      const sendButton = form.querySelector('[data-otp-send]');
      const verifyButton = form.querySelector('[data-otp-verify]');
      const resendButton = form.querySelector('[data-otp-resend]');
      const finishButton = form.querySelector('[data-otp-finish]');

      form.style.setProperty('--otp-code-digits', String(this.codeDigits(form)));

      phoneInput?.addEventListener('input', () => {
        phoneInput.value = this.formatPhoneInput(phoneInput.value);
        form.querySelector('[data-otp-clear-phone]').hidden = !phoneInput.value;
        this.clearError(form);
        this.setFieldError(form.querySelector('[data-otp-phone-field]'), false);
      });
      phoneInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          sendButton?.click();
        }
      });
      form.querySelector('[data-otp-clear-phone]')?.addEventListener('click', () => {
        phoneInput.value = '';
        form.querySelector('[data-otp-clear-phone]').hidden = true;
        phoneInput.focus();
      });

      codeInput?.addEventListener('input', () => {
        codeInput.value = this.digits(codeInput.value).slice(0, this.codeDigits(form));
        this.renderCode(form, codeInput.value);
        this.clearError(form);
        this.setFieldError(form.querySelector('[data-otp-code-field]'), false);
        this.updateVerifyButton(form, state, codeInput, verifyButton);
      });
      codeInput?.addEventListener('focus', () => this.renderCode(form, codeInput.value, true));
      codeInput?.addEventListener('blur', () => this.renderCode(form, codeInput.value));
      codeInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (!verifyButton?.disabled) verifyButton.click();
        }
      });
      form.querySelector('[data-otp-code-field]')?.addEventListener('click', () => codeInput?.focus());

      emailInput?.addEventListener('input', () => {
        emailInput.removeAttribute('aria-invalid');
        this.clearError(form);
        this.updateVerifyButton(form, state, codeInput, verifyButton);
      });
      form.querySelector('[data-otp-terms]')?.addEventListener('change', (event) => {
        event.currentTarget.closest('.le-auth__check')?.removeAttribute('data-error');
        this.clearError(form);
        this.updateVerifyButton(form, state, codeInput, verifyButton);
      });

      sendButton?.addEventListener('click', () => this.sendCode(form, state, phoneInput, sendButton));
      resendButton?.addEventListener('click', () => this.requestCode(form, state, resendButton));
      verifyButton?.addEventListener('click', () => this.verifyCode(form, state, codeInput, verifyButton));
      finishButton?.addEventListener('click', () => this.finishLogin(form, state, finishButton));
      form.querySelector('[data-otp-back]')?.addEventListener('click', () => this.goBack(form, state));
      form.querySelector('.le-auth__backdrop')?.addEventListener('click', () => {
        if (!state.redirecting) this.close(form, state);
      });

      this.setStep(form, 'phone');
      if (form.dataset.otpInitiallyOpen === 'true') {
        this.open(form, null, true);
      } else {
        form.hidden = true;
        form.setAttribute('aria-hidden', 'true');
      }
    }

    open(form, trigger = null, preserveReturnTo = false) {
      const state = this.formStates.get(form);
      if (!state) return;

      this.reset(form, state);
      this.lastTrigger = trigger;
      this.activeForm = form;

      if (!preserveReturnTo) {
        form.dataset.otpReturnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      }

      form.hidden = false;
      form.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('le-auth-open');
      requestAnimationFrame(() => form.querySelector('[data-otp-phone]')?.focus());
    }

    close(form, state = this.formStates.get(form)) {
      if (!form || !state) return;

      state.abortController?.abort();
      state.requestSequence += 1;
      this.stopResendCountdown(state);
      form.hidden = true;
      form.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('le-auth-open');
      this.activeForm = null;

      const trigger = this.lastTrigger;
      this.lastTrigger = null;
      requestAnimationFrame(() => trigger?.focus?.());
    }

    reset(form, state) {
      state.abortController?.abort();
      state.requestSequence += 1;
      state.flow = '';
      state.oidcLoginHint = '';
      state.phone = '';
      state.redirecting = false;
      state.requestId = '';
      this.stopResendCountdown(state);
      this.clearError(form);
      this.clearValidation(form);

      const phoneInput = form.querySelector('[data-otp-phone]');
      const codeInput = form.querySelector('[data-otp-code]');
      const emailInput = form.querySelector('[data-otp-email]');
      if (phoneInput) phoneInput.value = '';
      if (codeInput) codeInput.value = '';
      if (emailInput) emailInput.value = '';
      const termsInput = form.querySelector('[data-otp-terms]');
      const marketingInput = form.querySelector('[data-otp-marketing]');
      if (termsInput) termsInput.checked = false;
      if (marketingInput) marketingInput.checked = false;
      form.querySelector('[data-otp-clear-phone]')?.setAttribute('hidden', '');
      const sendButton = form.querySelector('[data-otp-send]');
      const verifyButton = form.querySelector('[data-otp-verify]');
      this.setLoading(sendButton, false);
      this.renderCode(form, '');
      this.configureFlow(form, 'existing');
      this.setStep(form, 'phone');
      this.updateVerifyButton(form, state, codeInput, verifyButton);
    }

    async sendCode(form, state, phoneInput, button) {
      const local = this.extractLocalDigits(phoneInput?.value || '');
      this.clearError(form);
      this.setFieldError(form.querySelector('[data-otp-phone-field]'), false);

      if (!this.isValidLocalPhone(local)) {
        this.setFieldError(form.querySelector('[data-otp-phone-field]'), true);
        this.showError(form, form.dataset.otpPhoneError);
        return;
      }

      state.phone = `+971${local}`;
      await this.requestCode(form, state, button);
    }

    async requestCode(form, state, button) {
      if (!state.phone || state.redirecting) return;

      const sequence = ++state.requestSequence;
      state.abortController?.abort();
      state.abortController = new AbortController();
      this.setLoading(button, true);
      this.clearError(form);

      try {
        const result = await this.requestJson(
          form,
          '/auth/otp/request',
          {
            market: form.dataset.otpMarket || 'AE',
            phone: state.phone,
            purpose: 'login',
          },
          state.abortController.signal,
        );
        if (sequence !== state.requestSequence) return;

        state.requestId = result.requestId || '';
        state.flow = result.flow === 'registration' ? 'registration' : 'existing';
        state.oidcLoginHint = '';
        form.querySelector('[data-otp-readable-phone]').textContent = this.formatReadablePhone(state.phone);
        form.querySelector('[data-otp-code]').value = '';
        form.querySelector('[data-otp-email]').value = '';
        form.querySelector('[data-otp-terms]').checked = false;
        form.querySelector('[data-otp-marketing]').checked = false;
        form.querySelector('[data-otp-mock]').hidden = !result.mocked;
        this.configureFlow(form, state.flow);
        this.renderCode(form, '');
        this.startResendCountdown(form, state);
        this.setStep(form, 'code');
        this.updateVerifyButton(form, state, form.querySelector('[data-otp-code]'), form.querySelector('[data-otp-verify]'));
        form.querySelector('[data-otp-code]')?.focus();
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.showError(form, this.errorMessage(form, error, 'request'));
      } finally {
        if (sequence === state.requestSequence) this.setLoading(button, false);
      }
    }

    async verifyCode(form, state, codeInput, button) {
      if (state.redirecting) return;

      const code = this.digits(codeInput?.value || '').slice(0, this.codeDigits(form));
      const emailInput = form.querySelector('[data-otp-email]');
      const termsInput = form.querySelector('[data-otp-terms]');
      this.clearError(form);
      this.clearValidation(form);

      if (code.length < this.codeDigits(form)) {
        this.setFieldError(form.querySelector('[data-otp-code-field]'), true);
        this.showError(form, form.dataset.otpIncompleteError);
        return;
      }

      const email = this.normalizeEmail(emailInput?.value);
      if (state.flow === 'registration' && !email) {
        emailInput?.setAttribute('aria-invalid', 'true');
        this.showError(form, form.dataset.otpEmailRequiredError);
        emailInput?.focus();
        return;
      }
      if (state.flow === 'registration' && !this.isValidEmail(email)) {
        emailInput?.setAttribute('aria-invalid', 'true');
        this.showError(form, form.dataset.otpEmailInvalidError);
        emailInput?.focus();
        return;
      }
      if (state.flow === 'registration' && !termsInput?.checked) {
        termsInput?.closest('.le-auth__check')?.setAttribute('data-error', 'true');
        this.showError(form, form.dataset.otpTermsError);
        termsInput?.focus();
        return;
      }

      this.setLoading(button, true);
      try {
        const result = await this.requestJson(form, '/auth/otp/verify', {
          requestId: state.requestId,
          code,
          email: state.flow === 'registration' ? email : undefined,
          phone: state.phone,
          termsAccepted: Boolean(termsInput?.checked),
          marketingAccepted: Boolean(form.querySelector('[data-otp-marketing]')?.checked),
        });

        state.oidcLoginHint = result.oidcLoginHint || '';
        if (!state.oidcLoginHint) throw new OtpServiceError(form.dataset.otpVerifyError, 502, 'oidc_email_missing');

        if (result.nextStep === 'welcome' || result.requiresActivation) {
          this.stopResendCountdown(state);
          this.setStep(form, 'welcome');
          return;
        }

        this.startRedirect(form, state, button);
      } catch (error) {
        this.setFieldError(form.querySelector('[data-otp-code-field]'), this.isCodeError(error));
        this.showError(form, this.errorMessage(form, error, 'verify'));
      } finally {
        if (!state.redirecting) {
          this.setLoading(button, false);
          this.updateVerifyButton(form, state, codeInput, button);
        }
      }
    }

    finishLogin(form, state, button) {
      if (state.redirecting) return;
      this.clearError(form);

      if (!state.oidcLoginHint) {
        this.showError(form, form.dataset.otpVerifyError);
        return;
      }

      this.setLoading(button, true);
      this.startRedirect(form, state, button);
    }

    startRedirect(form, state, button) {
      state.redirecting = true;
      this.stopResendCountdown(state);
      this.setButtonText(button, form.dataset.otpRedirectingText);
      this.redirectToLogin(form, state.oidcLoginHint);
    }

    redirectToLogin(form, loginHint) {
      const loginUrl = new URL(form.dataset.otpLoginUrl || '/customer_authentication/login', window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);

      currentParams.forEach((value, key) => {
        if (key !== 'login_hint') loginUrl.searchParams.set(key, value);
      });

      loginUrl.searchParams.set('return_to', this.returnToPath(form));
      loginUrl.searchParams.set('login_hint', loginHint);
      window.location.assign(loginUrl.toString());
    }

    returnToPath(form) {
      const explicit = new URLSearchParams(window.location.search).get('return_to');
      if (this.isAllowedReturnTo(explicit)) return explicit;

      const configured = form.dataset.otpReturnTo;
      if (configured !== '/' && this.isAllowedReturnTo(configured)) return configured;
      return '/';
    }

    isRelativePath(value) {
      return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');
    }

    isAllowedReturnTo(value) {
      if (this.isRelativePath(value)) return true;
      try {
        const url = new URL(value);
        return (
          url.protocol === 'https:' &&
          (url.origin === window.location.origin || url.hostname === 'shopify.com' || url.hostname.endsWith('.shopify.com'))
        );
      } catch (_error) {
        return false;
      }
    }

    goBack(form, state) {
      const visibleStep = form.querySelector('[data-otp-step]:not([hidden])')?.dataset.otpStep;
      if (visibleStep !== 'phone') {
        state.abortController?.abort();
        state.requestSequence += 1;
        state.requestId = '';
        state.oidcLoginHint = '';
        this.stopResendCountdown(state);
        this.clearError(form);
        this.clearValidation(form);
        this.setStep(form, 'phone');
        form.querySelector('[data-otp-phone]')?.focus();
        return;
      }

      const returnTo = this.returnToPath(form);
      if (this.activeForm === form) this.close(form, state);
      else if (returnTo && returnTo !== window.location.pathname) window.location.assign(returnTo);
      else window.location.assign('/');
    }

    async requestJson(form, path, body, signal) {
      const baseUrl = String(form.dataset.otpServiceUrl || '').replace(/\/$/, '');
      if (!baseUrl) throw new OtpServiceError(form.dataset.otpNetworkError, 0, 'network');

      let response;
      try {
        response = await fetch(`${baseUrl}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        });
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        throw new OtpServiceError(form.dataset.otpNetworkError, 0, 'network');
      }

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new OtpServiceError(payload?.error?.message || form.dataset.otpVerifyError, response.status, payload?.error?.code);
      }
      return payload;
    }

    configureFlow(form, flow) {
      const registration = flow === 'registration';
      form.querySelector('[data-otp-registration]').hidden = !registration;
      form.querySelector('[data-otp-registration-legal]').hidden = !registration;
      const buttonLabel = form.querySelector('[data-otp-verify] [data-otp-button-label]');
      if (buttonLabel) {
        buttonLabel.textContent = registration ? buttonLabel.dataset.otpRegisterText : buttonLabel.dataset.otpLoginText;
      }
    }

    setStep(form, step) {
      form.dataset.currentStep = step;
      this.elements(form, '[data-otp-step]').forEach((element) => {
        element.hidden = element.dataset.otpStep !== step;
      });

      const title = form.querySelector('[data-otp-title]');
      if (title) {
        if (step === 'phone') title.textContent = form.dataset.otpPhoneTitle || '';
        if (step === 'code') title.textContent = form.dataset.otpCodeTitle || '';
        if (step === 'welcome') title.textContent = '';
      }
      form.querySelector('.le-auth__header').hidden = step === 'welcome';
    }

    startResendCountdown(form, state) {
      this.stopResendCountdown(state);
      const seconds = Number.parseInt(form.dataset.otpResendSeconds || '60', 10);
      state.resendAvailableAt = Date.now() + Math.max(30, Math.min(seconds || 60, 300)) * 1000;

      const tick = () => {
        const remaining = Math.max(0, Math.ceil((state.resendAvailableAt - Date.now()) / 1000));
        const countdown = form.querySelector('[data-otp-countdown]');
        const resend = form.querySelector('[data-otp-resend]');
        if (countdown) {
          countdown.textContent = remaining > 0 ? this.countdownText(form, remaining) : '';
          countdown.hidden = remaining <= 0;
        }
        if (resend) resend.hidden = remaining > 0;

        if (remaining > 0) state.timerId = window.setTimeout(tick, 250);
      };
      tick();
    }

    stopResendCountdown(state) {
      if (state.timerId) window.clearTimeout(state.timerId);
      state.timerId = 0;
    }

    countdownText(form, seconds) {
      const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
      const remainder = String(seconds % 60).padStart(2, '0');
      const template = form.dataset.otpCountdownTemplate || 'You can get a new code in __TIME__';
      return template.replace('__TIME__', `${minutes}:${remainder}`);
    }

    renderCode(form, value, focused = document.activeElement === form.querySelector('[data-otp-code]')) {
      const digits = this.digits(value);
      const cells = this.elements(form, '[data-otp-code-cell]');
      cells.forEach((cell, index) => {
        cell.textContent = digits[index] || '';
        cell.dataset.active = String(focused && index === Math.min(digits.length, cells.length - 1));
      });
    }

    showError(form, message) {
      const visibleStep = form.querySelector('[data-otp-step]:not([hidden])');
      const error = visibleStep?.querySelector('[data-otp-error]');
      if (!error) return;
      error.textContent = message || form.dataset.otpVerifyError || '';
      error.hidden = false;
    }

    clearError(form) {
      this.elements(form, '[data-otp-error]').forEach((error) => {
        error.textContent = '';
        error.hidden = true;
      });
    }

    clearValidation(form) {
      form.querySelectorAll('[aria-invalid="true"]').forEach((element) => element.removeAttribute('aria-invalid'));
      form.querySelectorAll('[data-error="true"]').forEach((element) => element.removeAttribute('data-error'));
    }

    setFieldError(element, error) {
      if (!element) return;
      if (error) element.dataset.error = 'true';
      else element.removeAttribute('data-error');
    }

    setLoading(button, loading) {
      if (!button) return;
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    setButtonText(button, text) {
      const label = button?.querySelector('[data-otp-button-label]');
      if (label && text) label.textContent = text;
    }

    updateVerifyButton(form, state, codeInput, button) {
      if (!button) return;
      const hasCode = this.digits(codeInput?.value || '').length === this.codeDigits(form);
      button.disabled = state.redirecting || !hasCode;
    }

    errorMessage(form, error, fallbackKind) {
      if (error instanceof OtpServiceError) {
        const datasetKey = ERROR_DATASET_KEYS[error.code];
        if (datasetKey && form.dataset[datasetKey]) return form.dataset[datasetKey];
        if (error.code === 'network') return form.dataset.otpNetworkError;
      }
      return fallbackKind === 'request' ? form.dataset.otpRequestError : form.dataset.otpVerifyError;
    }

    isCodeError(error) {
      return ['already_used', 'expired', 'invalid_code', 'not_found', 'too_many_attempts'].includes(error?.code);
    }

    codeDigits(form) {
      const digits = Number.parseInt(form.dataset.otpCodeDigits || '4', 10);
      return Number.isFinite(digits) ? Math.min(Math.max(digits, 4), 8) : 4;
    }

    digits(value) {
      return String(value || '').replace(/\D/g, '');
    }

    normalizeEmail(value) {
      return String(value || '').trim().toLowerCase();
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    extractLocalDigits(value) {
      const digits = this.digits(value);
      const rawLocal = digits.startsWith('971') ? digits.slice(3) : digits;
      const local = rawLocal.startsWith('0') && rawLocal.length > 9 ? rawLocal.slice(1) : rawLocal;
      return local.slice(0, 9);
    }

    clampLocalDigits(value) {
      const local = this.extractLocalDigits(value);
      const prefixes = ['50', '52', '54', '55', '56', '58'];
      if (!local) return '';
      if (local[0] !== '5') return '';
      if (local.length > 1 && !prefixes.includes(local.slice(0, 2))) return local.slice(0, 1);
      return local;
    }

    isValidLocalPhone(local) {
      return local.length === 9 && ['50', '52', '54', '55', '56', '58'].includes(local.slice(0, 2));
    }

    formatPhoneInput(value) {
      const local = this.clampLocalDigits(value);
      return [local.slice(0, 2), local.slice(2, 5), local.slice(5, 9)].filter(Boolean).join(' ');
    }

    formatReadablePhone(value) {
      return ['+971', this.formatPhoneInput(value)].filter(Boolean).join(' ');
    }

    elements(root, selector) {
      const elements = [];
      if (root instanceof Element && root.matches(selector)) elements.push(root);
      root.querySelectorAll?.(selector).forEach((element) => elements.push(element));
      return elements;
    }
  }

  let controller;
  const bind = (root) => {
    if (!controller) controller = new LetoileOtpAuth(root);
    else controller.bind(root);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bind(document), { once: true });
  } else {
    bind(document);
  }
  document.addEventListener('shopify:section:load', (event) => bind(event.target));
})();
